/**
 * Reassign a repair order's RESPONSIBLE ADVISOR (not custody — custody keeps
 * its own "Reassign Advisor" in the Command Center). Candidates are the
 * shop's front-office members by identity (useFohCapableMembers: memberships
 * + work roles, multi-role aware), never names typed in.
 */
import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FohCapableMember } from '@/hooks/tickets/useFohCapableMembers';

export interface AdvisorAssignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roLabel: string;
  currentAdvisorUserId: string | null;
  members: readonly FohCapableMember[];
  pending?: boolean;
  onConfirm: (advisorUserId: string | null) => void;
}

export function AdvisorAssignDialog({
  open, onOpenChange, roLabel, currentAdvisorUserId, members, pending = false, onConfirm,
}: AdvisorAssignDialogProps) {
  const [choice, setChoice] = useState<string | null>(currentAdvisorUserId);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="advisor-assign-dialog">
        <DialogHeader>
          <DialogTitle>Advisor for {roLabel}</DialogTitle>
          <DialogDescription>
            The responsible advisor stays with this repair order through every handoff until someone changes it here.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-2 max-h-[50vh] overflow-y-auto" role="radiogroup">
          {members.map((m) => (
            <button
              key={m.userId}
              type="button"
              role="radio"
              aria-checked={choice === m.userId}
              data-testid={`advisor-option-${m.userId}`}
              onClick={() => setChoice(m.userId)}
              className={cn(
                'flex items-center justify-between rounded-md border px-3 py-3 text-left text-sm min-h-11',
                choice === m.userId ? 'border-primary bg-primary/10' : 'hover:bg-muted',
              )}
            >
              <span className="font-medium">{m.displayName}</span>
              {m.userId === currentAdvisorUserId && <span className="text-xs text-muted-foreground">current</span>}
            </button>
          ))}
          {currentAdvisorUserId && (
            <button
              type="button"
              role="radio"
              aria-checked={choice === null}
              data-testid="advisor-option-unassigned"
              onClick={() => setChoice(null)}
              className={cn('rounded-md border px-3 py-3 text-left text-sm min-h-11',
                choice === null ? 'border-primary bg-primary/10' : 'hover:bg-muted')}
            >
              Unassigned
            </button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            data-testid="advisor-assign-confirm"
            disabled={pending || choice === currentAdvisorUserId}
            onClick={() => onConfirm(choice)}
          >
            {pending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
